import mitt from "@livechat/mitt";

import { stringifyQueryParams } from "helpers/url";

import backo from "./backo";
import * as readyStates from "./interfaces";
// eslint-disable-line import/no-duplicates
import { ReadyState } from "./interfaces";

// eslint-disable-line import/no-duplicates

type Data = Parameters<WebSocket["send"]>[0];

// this should be type Event<Incoming extends Data> = ...
// but TS currently is not able to utilize our constrained generic to resolve conditional type down the road
// see https://github.com/Microsoft/TypeScript/issues/23132
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/naming-convention
interface Events {
  connect: undefined;
  disconnect: undefined;
  message: Data;
}

export type WebSocketManager<Incoming extends Data, Outgoing extends Data> = {
  connect(): void;
  destroy(): void;
  disconnect(): void;
  reconnect(delay?: number): void;
  emit(data: Outgoing): void;
  getReadyState(): ReadyState;
} & {
  // this should just use this Pick, but it's not quite possible because of the mentioned Microsoft/TypeScript#23132
  // Pick<Mitt<Events<Incoming>>, 'on' | 'off' >
  on(event: "connect", handler: () => void): void;
  on(event: "disconnect", handler: () => void): void;
  on(event: "message", handler: (message: Incoming) => void): void;

  off(event: "connect", handler: () => void): void;
  off(event: "disconnect", handler: () => void): void;
  off(event: "message", handler: (message: Incoming) => void): void;

  off(): void;
};

const createWebSocketManager = <Incoming extends Data, Outgoing extends Data>(
  url: string,
  { query }: { query?: { [key: string]: string } } = {}
): WebSocketManager<Incoming, Outgoing> => {
  const finalUrl = `${url}${!query ? "" : `?${stringifyQueryParams(query)}`}`;

  // @ts-expect-error TS(2344)
  const emitter = mitt<Events>();
  const backoff = backo({
    min: 1000,
    max: 5000,
    jitter: 0.5,
  });

  let state: ReadyState = readyStates.CLOSED;
  let reconnectTimer: ReturnType<typeof setTimeout>;
  let socket: WebSocket | null = null;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const openListener = () => {
    state = readyStates.OPEN;
    backoff.reset();
    emitter.emit("connect");
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const closeListener = () => {
    state = readyStates.CLOSED;
    socket = null;
    emitter.emit("disconnect");
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    reconnect();
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const messageListener = ({ data }: { data: Incoming }) => {
    emitter.emit("message", data);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const addEventListeners = (instance: WebSocket) => {
    instance.addEventListener("open", openListener);
    instance.addEventListener("close", closeListener);
    instance.addEventListener("message", messageListener);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const removeEventListeners = (instance: WebSocket) => {
    instance.removeEventListener("open", openListener);
    instance.removeEventListener("close", closeListener);
    instance.removeEventListener("message", messageListener);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const close = () => {
    state = readyStates.CLOSED;

    if (!socket) {
      return;
    }

    removeEventListeners(socket);
    socket.close();
    socket = null;
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const connect = () => {
    state = readyStates.CONNECTING;

    socket = new WebSocket(finalUrl);
    addEventListeners(socket);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const reconnect = (delay = backoff.duration()) => {
    clearTimeout(reconnectTimer);

    if (delay === 0) {
      connect();
      return;
    }

    reconnectTimer = setTimeout(connect, delay);
  };

  const managedSocket = {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    connect() {
      if (state !== readyStates.CLOSED) {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line no-console
        console.error("Socket is already open or connecting.");
        return;
      }
      clearTimeout(reconnectTimer);
      connect();
    },
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    destroy() {
      managedSocket.off();
      managedSocket.disconnect();
    },
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    disconnect() {
      state = readyStates.CLOSED;
      clearTimeout(reconnectTimer);
      close();
    },
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    reconnect(delay?: number) {
      close();
      reconnect(delay);
    },
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    emit(data: Outgoing) {
      if (state !== readyStates.OPEN) {
        throw new Error("Socket is not connected.");
      }

      // if we are connected we can be sure that socket is defined
      (socket as WebSocket).send(data);
    },
    getReadyState(): ReadyState {
      return state;
    },
    on: emitter.on,
    off: emitter.off,
  };

  return managedSocket;
};

export default createWebSocketManager;
